import { trackById } from 'src/app/shared/utils/track-by.util';
import { openFileInNewTab } from 'src/app/shared/utils/file-link.util';
import { downloadBlob } from 'src/app/shared/utils/download.util';
import { LabResultImportService, labResultTemplateFileName } from 'src/app/modals/lab-result-import-dialog/lab-result-import.service';
import { Component, Inject, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { IntegrationType } from 'src/app/enums/IntegrationType.enum';
import { LanguageEnum } from 'src/app/enums/language.enum';
import { SampleStatusColors, SampleStatusEnum } from 'src/app/enums/sampleStatus.enum';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { Lab, TAT } from 'src/app/models/lab.model';
import { Lookup } from 'src/app/models/lookup.model';
import { Sample } from 'src/app/models/sample.model';
import { LabRequisitionService } from 'src/app/services/lab-requisition.service';
import { LabService } from 'src/app/services/lab.service';
import { SampleService } from 'src/app/services/sample.service';
import { ToastService } from 'src/app/services/toast.service';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { StepConfig, StepperConfig } from 'src/app/shared/stepper/stepper.model';
import { EventService } from 'src/app/services/event.service';
import { EventKeys } from 'src/app/shared/constants/eventKeys.constants';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { LabRequisitionStatus } from 'src/app/enums/labRequisitionStatus.enum';
import { LabRequisition } from 'src/app/models/labRequisition.model';
import { Router } from '@angular/router';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { FileAttachmentService } from 'src/app/services/file-attachment.service';
import { UploadService } from 'src/app/services/upload.service';
import { generateGUID } from 'src/app/_helpers/guid.helper';
import { AssetTypeEnum } from 'src/app/enums/assetType.enum';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { CreatePayload } from 'src/app/shared/grid/crud.service';
import { FileTypeEnum } from 'src/app/enums/fileType.enum';
import { DocumentType } from 'src/app/enums/documentType.enum';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { AuthService } from 'src/app/services';
import { SecurityProfileEnum } from 'src/app/enums/securityProfile.enum';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { PdfExportService } from 'src/app/services/pdf-export.service';
import { TranslateService } from 'src/app/services/translate.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridView } from 'src/app/models/gridView.model';
import { getSampleGridIdForLab } from 'src/app/shared/constants/gridIds.constants';
import { LocalStorageConstants } from 'src/app/shared/constants/localStorage.constants';

export interface IGenerateCocPopup extends IFormDialogData {
  samples?: Sample[];
  isFromSamples?: boolean;
}

interface SampleVm extends Sample {
    agentNamesStr: string;
    samplingDateStr: string;
    volumeLitersStr: string;
    runTimeMinutesStr: string;
    flowRateLPerMinStr: string;
}

const LABS_WITH_ACCOUNT_STEP = [IntegrationType.Lims, IntegrationType.GalsonLab, IntegrationType.AlsLab];

@Component({
  standalone: false,
  selector: 'app-generate-coc-popup',
  templateUrl: './generate-coc-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './generate-coc-popup.component.scss'
})
export class GenerateCocPopupComponent implements OnInit {
  readonly IntegrationType = IntegrationType;
  @ViewChild('account') accountTpl!: TemplateRef<any>;
  @ViewChild('accountInfo') accountInfoTpl!: TemplateRef<any>;
  @ViewChild('alsQuote') alsQuoteTpl!: TemplateRef<any>;
  @ViewChild('tat') tatTpl!: TemplateRef<any>;
  @ViewChild('collection') collectionTpl!: TemplateRef<any>;
  @ViewChild('sampling') samplingTpl!: TemplateRef<any>;
  @ViewChild('finalize') finalizeTpl!: TemplateRef<any>;
  stepperConfig!: StepperConfig;
  labDetails: Lab;
  shownSampleDetails: {
    siteNames: string[];
    sampledby: string[];
    surveyNames: string[];
    jurisdictionNames: string[];
  }

  stateOptions = [
    { value: 'state1', label: 'State 1' },
    { value: 'state2', label: 'State 2' },
  ];
  samplesList: SampleVm[] = [];
  selectedSamples: SampleVm[] = [];
  sampleColumns: string[] = [];
  tatColumns = [];
  selectedLabId: string;
  labsList: Lookup[] = [];
  sampleStatusDetails = {
    label: getNamedEnum(SampleStatusEnum)[SampleStatusEnum.AwaitingLabSubmission],
    color: SampleStatusColors[SampleStatusEnum.AwaitingLabSubmission]
  }
  labRequisitionFields: LabRequisition;
  exemptedSamples: Sample[] = [];
  blankCount = 0;
  totalSampleCount = 0;
  blankRuleBelowCount = false;
  blankRuleBelowRatio = false;
  blankRuleViolated = false;
  effectiveMinBlankCount = 2;
  effectiveMinBlankRatioPercent = 10;
  relinquishedBySignature = '';
  relinquishedByDate = '';
  relinquishedByTime = '';

  private readonly DEFAULT_MIN_BLANK_COUNT = 2;          // mirrors BE Constants
  private readonly DEFAULT_MIN_BLANK_RATIO_PERCENT = 10; // client-side warning only — server authoritative
  isFinalStep: boolean;
  loading: boolean;
  userDateWithTimeFormat: string;
  tenantLogoUrl: string;
  alternateSampleLabel: string;
  private readonly alternateSampleColumn = nameOf<Sample>(x => x.alternateSampleNumber);
  readonly defaultSampleColumns: string[] = [
    nameOf<Sample>(x => x.id),
    nameOf<Sample>(x => x.sampleNumber),
    nameOf<IhLabResult>(x => x.masterAgentName),
    nameOf<IhLabResult>(x => x.agentGroupId),
    nameOf<Sample>(x => x.startDate),
    nameOf<Sample>(x => x.sampleVolume),
    nameOf<Sample>(x => x.runTime),
    nameOf<Sample>(x => x.flowRate),
    nameOf<Sample>(x => x.analyticalMethodName),
  ];
  drawBox(
    doc: any,
    x: number,
    y: number,
    w: number,
    h: number,
    radius = 4
  ) {
    doc.setDrawColor(180);
    doc.setLineWidth(0.5);
    doc.roundedRect(x, y, w, h, radius, radius);
  }

  constructor(@Inject(FLYOUT_DATA) public data: IGenerateCocPopup,
    private labService: LabService,
    private sampleService: SampleService,
    private labRequisitionService: LabRequisitionService,
    private toastService: ToastService,
    private eventService: EventService,
    private router: Router,
    private dateFormatService: DateFormatService,
    private agentGroupService: AgentGroupService,
    private uploadService: UploadService,
    private fileAttachmentService: FileAttachmentService,
    private organizationLevelService: OrganizationLevelService,
    private authService: AuthService,
    private pdfExportService: PdfExportService,
    private translate: TranslateService,
    private gridViewService: GridViewService,
    private labResultImportService: LabResultImportService,
    private valueConversionService: ValueConversionService) {
    this.sampleColumns = [...this.defaultSampleColumns];
    this.tatColumns = [
      nameOf<TAT>(x => x.turnAroundTime),
      nameOf<TAT>(x => x.surcharge),
    ]
    this.labRequisitionFields = new LabRequisition();
  }

  ngOnInit(): void {
    this.loading = true;
    const { id, isFromSamples } = this.data;
    this.userDateWithTimeFormat = this.dateFormatService.userDateWithTimeFormat;

    forkJoin({
      labRequisitionData: id
        ? this.labRequisitionService.getById(id)
        : of({} as LabRequisition),
      sampleFieldsConfig: this.sampleService.getFieldConfiguration(),
      labList: id ? of([]) : this.labService.getAll()
    }).subscribe({
      next: async (result) => {

        this.labsList = result.labList;
        this.labRequisitionFields = result.labRequisitionData;

        this.alternateSampleLabel =
          result.sampleFieldsConfig['alternateSampleNumber']?.displayName ||
          this.translate.getDynamicTranslation('Alternate Sample Number');

        let samples: Sample[] = [];

        if (id && this.labRequisitionFields?.sampleIds?.length) {

          // Fetch samples directly by saved IDs
          const sampleRequests = this.labRequisitionFields.sampleIds.map(sampleId =>
            this.sampleService.getById(sampleId)
          );

          samples = await firstValueFrom(forkJoin(sampleRequests));

        } else if (isFromSamples) {

          samples = this.data.samples || [];

        } else {

          const sampleFilter = JSON.stringify({
            sampleStatus: SampleStatusEnum.AwaitingLabSubmission
          });

          const response = await firstValueFrom(
            this.sampleService.get('', 'id', 'asc', 0, 100, sampleFilter)
          );

          samples = response.data;
        }

        this.samplesList = samples.map(s => this.toSampleVm(s));

        if (id) {
          this.selectedLabId = this.labRequisitionFields.labId;

          this.data.samples = samples.filter(s =>
            this.labRequisitionFields.sampleIds.includes(s.id)
          );

          this.selectedSamples = this.samplesList.filter(
            s => s.sampleStatus === SampleStatusEnum.AwaitingLabSubmission &&
              this.labRequisitionFields.sampleIds.includes(s.id)
          );

          this.showSampleSelectionTable();
        }

        this.loading = false;
      },
      error: (error) => {
        return this.toastService.showError(error);
      }
    });
    const currentUser = this.authService.getCurrentUser();
    this.tenantLogoUrl = currentUser?.tenantLogoUrl;
    // 3-line block: full name, email, then the "Signed by LEAP" system label. \n-joined for the PDF cell.
    this.relinquishedBySignature = [
      currentUser?.userFullName ?? '',
      currentUser?.userEmail ?? '',
      this.translate.getDynamicTranslation('Signed by LEAP')
    ].filter(Boolean).join('\n');
    this.relinquishedByDate = this.dateFormatService.formatDateTimeToString(new Date(), this.dateFormatService.userDateFormat);
    this.relinquishedByTime = this.dateFormatService.formatDateTimeToString(new Date(), this.dateFormatService.userTimeFormat);
  }

  private computeBlankRuleState(): void {
    this.totalSampleCount = this.selectedSamples.length;
    this.blankCount = this.selectedSamples.filter(
      s => s.sampleCategory === SampleCategoryEnum.Blank
    ).length;

    const enforceBlankRules = this.labDetails?.enforceBlankRules ?? false;
    this.effectiveMinBlankCount = this.labDetails?.minBlankCount ?? this.DEFAULT_MIN_BLANK_COUNT;
    this.effectiveMinBlankRatioPercent = this.labDetails?.minBlankRatioPercent ?? this.DEFAULT_MIN_BLANK_RATIO_PERCENT;

    this.blankRuleBelowCount = enforceBlankRules && this.blankCount < this.effectiveMinBlankCount;
    this.blankRuleBelowRatio = enforceBlankRules && this.totalSampleCount > 0
      && (this.blankCount * 100) < (this.effectiveMinBlankRatioPercent * this.totalSampleCount);
    this.blankRuleViolated = this.blankRuleBelowCount || this.blankRuleBelowRatio;
  }

  setStepper() {
    if (this.data.samples) {
      this.exemptedSamples = this.data.samples.filter(sample => sample.sampleStatus !== SampleStatusEnum.AwaitingLabSubmission);
    }
    this.computeBlankRuleState();

    this.stepperConfig = {
      orientation: 'horizontal',
      linear: true,
      steps: [
        {
          id: 'sampling', label: 'Sampling', template: this.samplingTpl,
          canProceed: () => {
            if (!this.selectedSamples.length) this.toastService.displayErrorMessage("Please select at least one sample", true);
            if (this.selectedSamples.length > 0 && this.labDetails.labIntegrationType === IntegrationType.Lims) {
              this.generateBarCode();
            }
            if (this.selectedSamples.length) this.setParentSites();
            return this.selectedSamples.length > 0;
          }
        },
        {
          id: 'account', label: 'Account', template: this.accountTpl,
          visible: () => this.labDetails.labIntegrationType === IntegrationType.Lims,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ],
          canProceed: () => {
            const num = this.labRequisitionFields.labRequisitionNumber;
            if (!num) return this.toastService.displayErrorMessage("Please enter a Lab Requisition Number", true), false;
            if (num.length > 20) return this.toastService.displayErrorMessage("Lab Requisition Number cannot exceed 20 characters", true), false;
            return true;
          }
        },
        {
          id: 'accountInfo', label: 'Account Information', template: this.accountInfoTpl,
          // Labs without an account step of their own (not LIMS or ALS) ask for the Lab Requisition Number here, like Galson.
          visible: () => this.labDetails.labIntegrationType === IntegrationType.GalsonLab
            || !LABS_WITH_ACCOUNT_STEP.includes(this.labDetails.labIntegrationType),
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ],
          canProceed: () => {
            const num = this.labRequisitionFields.labRequisitionNumber;
            if (!num) return this.toastService.displayErrorMessage("Please enter a Lab Requisition Number", true), false;
            if (num.length > 20) return this.toastService.displayErrorMessage("Lab Requisition Number cannot exceed 20 characters", true), false;
            return true;
          }
        },
        {
          // Label is hardcoded to match all other steps in this component; component-wide i18n of step labels is out of scope
          id: 'alsQuote', label: 'ALS Quote', template: this.alsQuoteTpl,
          visible: () => this.labDetails.labIntegrationType === IntegrationType.AlsLab,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ],
          canProceed: () => {
            const num = this.labRequisitionFields.labRequisitionNumber;
            if (!num) return this.toastService.displayErrorMessage("Please enter a Lab Requisition Number", true), false;
            if (num.length > 20) return this.toastService.displayErrorMessage("Lab Requisition Number cannot exceed 20 characters", true), false;
            return true;
          }
        },
        {
          id: 'tat', label: 'TAT', template: this.tatTpl,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ]
        },
        {
          id: 'collection', label: 'Collection', template: this.collectionTpl,
          buttons: [
            { label: 'Save Progress Exit', action: () => this.saveProgress() },
          ]
        },
        {
          id: 'finalize', label: 'Finalize', template: this.finalizeTpl,
          buttons: [
            { label: 'Save Progress & View PDF', action: () => this.saveProgress(true) },
            {
              label: 'Finalize COC and Print',
              disabled: () => this.blankRuleViolated,
              action: () => {
                if (this.blankRuleViolated) {
                  this.toastService.displayErrorMessage(
                    this.blankRuleBelowCount
                      ? 'This Lab Requisition requires at least {{count}} blank sample(s) but has {{actual}}.'
                      : 'Blank samples must be at least {{ratio}}% of samples on this requisition.',
                    true,
                    this.blankRuleBelowCount
                      ? { count: this.effectiveMinBlankCount, actual: this.blankCount }
                      : { ratio: this.effectiveMinBlankRatioPercent }
                  );
                  return;
                }
                this.loading = true;
                this.saveProgress(true, true);
              }
            }
          ]
        },
      ]
    };
    this.labRequisitionFields = {
      ...this.labRequisitionFields,
      sampleIds: this.selectedSamples.map(sample => sample.id)
    }
  }

  generateBarCode() {
    const agentGroupIds = [
      ...new Set(this.selectedSamples
        .flatMap(s => s.ihLabResults?.map(r => r.agentGroupId) || [])
        .filter(Boolean))];
    if (agentGroupIds.length) {
      this.agentGroupService.getByIds(agentGroupIds)
        .subscribe(agentGroups => {
          const agentGroupMap = new Map(agentGroups.map(agentGroup => [agentGroup.id, agentGroup]));
          this.selectedSamples.forEach(sample => {
            const firstResult = sample.ihLabResults?.[0];
            if (firstResult && agentGroupMap.has(firstResult.agentGroupId)) {
              const date = (sample.startDate as string).slice(0, 10).replace(/-/g, '');
              const lastSampleNumber = sample.sampleNumber.length <= 4 ? sample.sampleNumber : sample.sampleNumber.slice(-4);
              sample.barcodeContent = `${agentGroupMap.get(firstResult.agentGroupId)?.casId} ${date} ${lastSampleNumber}`;
            } else {
              sample.barcodeContent = undefined;
            }
          });
        });
    }
  }

  showSampleSelectionTable() {
    forkJoin({
      lab: this.labService.getById(this.selectedLabId),
      gridView: this.gridViewService.getDefaultView(getSampleGridIdForLab(this.selectedLabId)).pipe(catchError(() => of(null)))
    }).subscribe({
      next: ({ lab, gridView }) => {
        this.sampleColumns = [...this.defaultSampleColumns];
        if (lab.labIntegrationType === IntegrationType.GalsonLab && !this.sampleColumns.includes(this.alternateSampleColumn)) {
          this.sampleColumns.splice(2, 0, this.alternateSampleColumn);
        }
        this.labDetails = lab;
        this.patchFormData();
        this.setStepper();
        if (this.data.id && lab.labIntegrationType === IntegrationType.Lims && this.selectedSamples.length > 0) {
          this.generateBarCode();
        }
        this.getlabOrgs();
        if (gridView?.settingsJson?.columns?.length > 0) {
          this.applyGridViewColumns(gridView);
        }
      },
      error: (error) => {
        this.loading = false;
        this.toastService.showError(error);
      }
    });
  }

  // Any lab can tick Alternate Sample Number in its sample grid settings; only Galson shows it by default.
  private applyGridViewColumns(view: GridView): void {
    const knownColumns = new Set([...this.sampleColumns, this.alternateSampleColumn]);
    const visibleColumns = view.settingsJson.columns
      .filter(col => col.visible && knownColumns.has(col.property))
      .sort((a, b) => a.order - b.order)
      .map(col => col.property);
    if (!visibleColumns.length) return;
    const idProp = nameOf<Sample>(x => x.id);
    if (!visibleColumns.includes(idProp)) {
      visibleColumns.unshift(idProp);
    }
    this.sampleColumns = visibleColumns;
  }

  patchFormData() {
    this.loading = false;
    this.labRequisitionFields = {
      ...this.labDetails, ...this.labRequisitionFields,
      labId: this.labDetails.id, id: this.data.id
    };
    this.updateShownSampleDetails();
  }

  getlabOrgs() {
    const labReqNumber = this.labRequisitionFields.labRequisitionNumber;
    const orgLevelId = this.labRequisitionFields.organizationLevelId ?? this.labDetails?.organizationLevelId;
    if (orgLevelId) {
      const applyRequisionNumber = (org: OrganizationLevel) => {
        if (org.labRequisitionNumberTemplate) {
          if (!labReqNumber) this.labRequisitionFields.labRequisitionNumber = "Autogenerated";
          this.labRequisitionFields.isAutogenerated = true;
        }
      };

      this.organizationLevelService.getById(orgLevelId).subscribe({
        next: (site) => applyRequisionNumber(site),
        error: (err) => {
          this.toastService.showError(err);
        }
      });
    }
  }


  isSelected(row: Sample): boolean {
    return this.selectedSamples.map(sample => sample.id).includes(row.id);
  }

  toggleSelection(row: any, checked: boolean) {
    if (checked) {
      this.selectedSamples.push(row);
    } else {
      this.selectedSamples = this.selectedSamples.filter(s => s.id !== row.id);
    }
    this.updateShownSampleDetails();
  }

  getUniqueAgentNames(ihLabResults: IhLabResult[]): string {
    return this.joinAgentNames(ihLabResults) || '-';
  }

  // The tenant agent name comes back in the user's language; the master agent name is English only.
  private joinAgentNames(ihLabResults: IhLabResult[] | undefined): string {
    return [...new Set((ihLabResults ?? []).map(r => r.agentName || r.masterAgentName).filter(Boolean))].join(', ');
  }

  isAllSelected(): boolean {
    return this.samplesList.length > 0 && this.selectedSamples.length === this.samplesList.length;
  }

  toggleAll(checked: boolean) {
    if (checked) {
      this.selectedSamples = [...this.samplesList];
    } else { this.selectedSamples = []; }
    this.updateShownSampleDetails();
  }

  private updateShownSampleDetails() {
    this.labRequisitionFields.sampleIds = this.selectedSamples.map(sample => sample.id);
    this.computeBlankRuleState();
  }

  async setParentSites() {
    const siteIds = Array.from(
      new Set(this.selectedSamples.map(s => s.organizationLevelId).filter(Boolean))
    );

    const siteNameMap = new Map<string, string>();

    const fetches = siteIds.map(async siteId => {
      try {
        const parentSite = await firstValueFrom(this.organizationLevelService.getParentSite(siteId));
        if (parentSite?.name) {
          siteNameMap.set(siteId, parentSite.name);
        }
      } catch (error) {
      }
    });
    await Promise.all(fetches);

    const siteNameSet = new Set(siteNameMap.values());
    const sampledBySet = new Set(this.selectedSamples.map(s => s.sampledByUsername).filter(Boolean));
    const surveySet = new Set(this.selectedSamples.map(s => s.surveyName).filter(Boolean));
    const jurisdictionSet = new Set(this.selectedSamples.flatMap(sample => sample.ihLabResults ?? [])
      .map(result => result.jurisdictionName).filter(Boolean));

    this.shownSampleDetails = {
      siteNames: Array.from(siteNameSet),
      sampledby: Array.from(sampledBySet),
      surveyNames: Array.from(surveySet),
      jurisdictionNames: Array.from(jurisdictionSet)
    };
  }

  selectTat(row: TAT) {
    this.labRequisitionFields.tatId = row.id;
  }

  isTatSelected(row: any): boolean {
    return this.labRequisitionFields.tatId === row.id;
  }

  onStepChanged(step: StepConfig) {
    this.isFinalStep = Boolean(step.id === 'finalize');
  }

  openSampleDetail(item: Sample) {
    window.open(this.router.serializeUrl(
      this.router.createUrlTree([`/dashboard/sample-details/${item.id}`])
    ), '_blank');
  }

  async generatePdfBlob(
    orientation: 'portrait' | 'landscape' = 'portrait'
  ): Promise<Blob> {
    const currentLang = localStorage.getItem(LocalStorageConstants.language);
    const isSpanish = currentLang === LanguageEnum['Spanish (Chile)'];
    const labelValueGap = isSpanish ? 5 : 8;
    const accountInfoGap = isSpanish ? 8 : 10;
    const accountInfoWidth = 100;

    const doc = await this.pdfExportService.createDocument(orientation);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageMargin = 40;
    const contentWidth = pageWidth - pageMargin * 2;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    const title = this.translate.getDynamicTranslation('Chain Of Custody');
    const titleY = 30;
    doc.text(title, pageWidth / 2, titleY, { align: 'center' });
    if (this.tenantLogoUrl) {
      try {
        if (this.tenantLogoUrl.startsWith('data:')) {
          doc.addImage(this.tenantLogoUrl, 'PNG', pageMargin, titleY - 12, 50, 50);
        }
      } catch (e) {
      }
    }
    let y = titleY + 18;
    const tatTableWidth = 140;
    const gap = 20;
    const alignedBoxX = pageMargin + tatTableWidth + gap;
    const alignedBoxW = pageWidth - alignedBoxX - pageMargin;

    await this.pdfExportService.addTable(
      doc,
      [this.translate.getDynamicTranslation('Turn Around Time'), this.translate.getDynamicTranslation('Surcharge')],
      [[
        this.labRequisitionFields.tatId
          ? this.labRequisitionFields.tats?.find(r => r.id === this.labRequisitionFields.tatId)?.turnAroundTime || ''
          : '',
        this.labRequisitionFields.tatId
          ? this.labRequisitionFields.tats?.find(r => r.id === this.labRequisitionFields.tatId)?.surcharge || ''
          : '',
      ]],
      {
        startY: y,
        theme: 'plain',
        headStyles: {
          fillColor: [235, 240, 250],
          fontSize: 10,
          halign: 'center',
        },
        bodyStyles: {
          fontSize: 10,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 30
        },
        columnStyles: {
          0: { cellWidth: tatTableWidth * 0.5 },
          1: { cellWidth: tatTableWidth * 0.5 }
        } as any
      }
    );

    const accountBoxHeight = 40;
    this.drawBox(doc, alignedBoxX, y, alignedBoxW, accountBoxHeight);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const isAlsIntegration = this.labDetails.labIntegrationType === IntegrationType.AlsLab;
    const accountLabel = isAlsIntegration ? 'ALS Quote Number:' : 'Account Information:';
    const accountValue = isAlsIntegration 
      ? (this.labDetails.labAccountNumber || '')
      : (this.labRequisitionFields.labAccountNumber || '');

    doc.setTextColor(120, 120, 120);
    doc.text(accountLabel, alignedBoxX + 20, y + 18);
    doc.setTextColor(0, 0, 0);
    doc.text(accountValue, alignedBoxX + 20 + accountInfoWidth + accountInfoGap, y + 18);

    doc.setTextColor(120, 120, 120);
    const labReqNumberValue = this.labRequisitionFields.labRequisitionNumber || '';
    const labReqNumberWidth = doc.getTextWidth(labReqNumberValue);
    const labReqKeyWidth = doc.getTextWidth(this.translate.getDynamicTranslation('Lab Requisition Number') + ':');
    const totalWidth = labReqKeyWidth + labReqNumberWidth + 15;
    const labReqKeyX = alignedBoxX + alignedBoxW - totalWidth;

    doc.setTextColor(120, 120, 120);
    doc.text(this.translate.getDynamicTranslation('Lab Requisition Number') + ':', labReqKeyX, y + 18);
    doc.setTextColor(0, 0, 0);
    doc.text(labReqNumberValue, alignedBoxX + alignedBoxW - 10, y + 18, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    y += accountBoxHeight + 5;

    const rightBoxWidth = (alignedBoxW - 10) / 2;
    const infoBoxHeight = 120;

    this.drawBox(doc, alignedBoxX, y, rightBoxWidth, infoBoxHeight);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const reportToContent = [
      `${this.translate.getDynamicTranslation('Report To')}: ${this.labRequisitionFields.reportTo || ''}`,
      `${this.translate.getDynamicTranslation('Company Name')}: ${this.labRequisitionFields.reportToCompanyName || ''}`,
      `${this.translate.getDynamicTranslation('Address')}: ${this.labRequisitionFields.reportToAddress1 || ''}`,
      `${this.translate.getDynamicTranslation('City, State, Zip')}: ${[
        this.labRequisitionFields.reportToCity,
        this.labRequisitionFields.reportToState,
        this.labRequisitionFields.reportToZip
      ].filter(Boolean).join(', ')}`,
      `${this.translate.getDynamicTranslation('Phone Number')}: ${this.labRequisitionFields.reportToPhoneNumber || ''}`,
      `${this.translate.getDynamicTranslation('Cell Number')}: ${this.labRequisitionFields.reportToCellNumber || ''}`,
      `${this.translate.getDynamicTranslation('Email (Report To)')}: ${this.labRequisitionFields.reportToEmail || ''}`,
      `${this.translate.getDynamicTranslation('Email EDD')}: ${this.labRequisitionFields.emailEdd || ''}`,
      `${this.translate.getDynamicTranslation('Report To Comments')}: ${this.labRequisitionFields.reportToComments || ''}`
    ].join('\n');

    const reportText = doc.splitTextToSize(reportToContent, rightBoxWidth - 20);
    doc.text(reportText, alignedBoxX + 10, y + 25);

    const invoiceBoxX = alignedBoxX + rightBoxWidth + 10;
    this.drawBox(doc, invoiceBoxX, y, rightBoxWidth, infoBoxHeight);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const invoiceToContent = [
      `${this.translate.getDynamicTranslation('Invoice To')}: ${this.labRequisitionFields.invoiceTo || ''}`,
      `${this.translate.getDynamicTranslation('Company Name')}: ${this.labRequisitionFields.invoiceCompanyName || ''}`,
      `${this.translate.getDynamicTranslation('Address')}: ${this.labRequisitionFields.invoiceAddress1 || ''}`,
      `${this.translate.getDynamicTranslation('Phone Number')}: ${this.labRequisitionFields.invoicePhoneNumber || ''}`,
      `${this.translate.getDynamicTranslation('Email (Invoice To)')}: ${this.labRequisitionFields.invoiceEmail || ''}`,
      `${this.translate.getDynamicTranslation('PO')}: ${this.labRequisitionFields.invoicePoNumber || ''}`,
      `${this.translate.getDynamicTranslation('Payment Method')}: ${this.labRequisitionFields.invoicePaymentInfo || ''}`,
      `${this.translate.getDynamicTranslation('Invoice To Comments')}: ${this.labRequisitionFields.invoiceComments || ''}`
    ].join('\n');

    const invoiceText = doc.splitTextToSize(invoiceToContent, rightBoxWidth - 20);
    doc.text(invoiceText, invoiceBoxX + 10, y + 25);

    y += infoBoxHeight + 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const siteNames = this.shownSampleDetails?.siteNames?.join(', ') || '';
    const surveyNames = this.shownSampleDetails?.surveyNames?.join(', ') || '';
    const sampledByNames = this.shownSampleDetails?.sampledby?.join(', ') || '';
    const jurisdictions = this.shownSampleDetails?.jurisdictionNames?.join(', ') || '';

    const startTextY = y + 18;
    const lineHeight = 14;
    const defaultBoxHeight = 45;

    const siteLines = doc.splitTextToSize(siteNames, 140);
    const surveyLines = doc.splitTextToSize(surveyNames, 130);
    const sampledByLines = doc.splitTextToSize(sampledByNames, 120);

    const jurisdictionArr = this.shownSampleDetails?.jurisdictionNames || [];
    const jurisdictionText = jurisdictions;
    const jurisdictionWidth = doc.getTextWidth(jurisdictionText);
    const inlineThreshold = 180;
    const isInline = jurisdictionArr.length <= 1 && jurisdictionWidth < inlineThreshold;

    const maxLines = Math.max(
      siteLines.length,
      surveyLines.length,
      sampledByLines.length
    );

    let siteBoxHeight =
      maxLines > 1
        ? startTextY + maxLines * lineHeight - y + 10
        : defaultBoxHeight;

    let extraHeight = 0;
    if (!isInline) {
      const jurisdictionLines = doc.splitTextToSize(jurisdictionText, contentWidth - 20);
      extraHeight = (jurisdictionLines.length * lineHeight) + 20;
      siteBoxHeight += extraHeight;
    }

    this.drawBox(doc, pageMargin, y, contentWidth, siteBoxHeight);
    doc.setTextColor(120, 120, 120);
    const siteNameLabel = this.translate.getDynamicTranslation('Site Name') + ':';
    doc.text(siteNameLabel, pageMargin + 10, startTextY);
    const siteNameLabelWidth = doc.getTextWidth(siteNameLabel);
    doc.setTextColor(0, 0, 0);
    doc.text(siteLines, pageMargin + 10 + siteNameLabelWidth + labelValueGap, startTextY);

    doc.setTextColor(120, 120, 120);
    const surveyLabel = this.translate.getDynamicTranslation('Survey') + ':';
    doc.text(surveyLabel, pageMargin + 200, startTextY);
    const surveyLabelWidth = doc.getTextWidth(surveyLabel);
    doc.setTextColor(0, 0, 0);
    doc.text(surveyLines, pageMargin + 200 + surveyLabelWidth + labelValueGap, startTextY);

    doc.setTextColor(120, 120, 120);
    const sampledByLabel = this.translate.getDynamicTranslation('Sampled By') + ':';
    doc.text(sampledByLabel, pageMargin + 410, startTextY);
    const sampledByLabelWidth = doc.getTextWidth(sampledByLabel);
    doc.setTextColor(0, 0, 0);
    doc.text(sampledByLines, pageMargin + 410 + sampledByLabelWidth + labelValueGap, startTextY);
    doc.setTextColor(0, 0, 0);

    if (isInline) {
      doc.setTextColor(120, 120, 120);
      const jurisdictionLabel = this.translate.getDynamicTranslation('Jurisdiction') + ':';
      doc.text(jurisdictionLabel, pageWidth - pageMargin - 150, startTextY);
      doc.setTextColor(0, 0, 0);
      doc.text(jurisdictionText, pageWidth - pageMargin - 30, startTextY, { align: 'right' });
      y += siteBoxHeight + 10;
    } else {
      const jurisdictionLines = doc.splitTextToSize(jurisdictionText, contentWidth - 20);
      const jurisdictionLabelY = startTextY + (maxLines * lineHeight) + 10;
      doc.setTextColor(120, 120, 120);
      const jurisdictionLabel = this.translate.getDynamicTranslation('Jurisdiction') + ':';
      doc.text(jurisdictionLabel, pageMargin + 10, jurisdictionLabelY);
      const jurisdictionLabelWidth = doc.getTextWidth(jurisdictionLabel);
      doc.setTextColor(0, 0, 0);
      doc.text(jurisdictionLines, pageMargin + 10 + jurisdictionLabelWidth + labelValueGap, jurisdictionLabelY);
      y += siteBoxHeight;
    }

    y += 60;

    const commentBoxHeight = 70;
    const commentGap = 15;
    const commentBoxWidth = (contentWidth - commentGap) / 2;

    this.drawBox(doc, pageMargin, y, commentBoxWidth, commentBoxHeight);

    doc.setFontSize(10);
    doc.setFont('helvetica');
    doc.text(this.translate.getDynamicTranslation('General Comments') + ':', pageMargin + 10, y + 15);

    this.drawBox(doc, pageMargin + commentBoxWidth + commentGap, y, commentBoxWidth, commentBoxHeight);

    doc.setFont('helvetica');
    doc.setFontSize(10);
    const industryText = doc.splitTextToSize(
      this.translate.getDynamicTranslation('List Description Of Industry Or Process/Interferences Present In Sampling Area') + ':',
      commentBoxWidth - 20
    );

    doc.text(industryText, pageMargin + commentBoxWidth + commentGap + 10, y + 15);

    y += 85;

    // Check if we need to add a page break before the samples table
    const estimatedTableHeight = this.selectedSamples.length * 50 + 40; // Approximate height
    const pageHeightForTable = doc.internal.pageSize.getHeight();

    if (y + estimatedTableHeight > pageHeightForTable - pageMargin) {
      // Add a new page if table won't fit
      doc.addPage();
      y = pageMargin;
    }

    // Pre-generate barcode image data for all samples
    const barcodeRowsMap = new Map<number, { sampleNumber: string; barcodeContent: string }>();
    const barcodeImageData = new Map<number, string>();
    const isLimsIntegration = this.labDetails.labIntegrationType === IntegrationType.Lims;

    if (isLimsIntegration) {
      for (let i = 0; i < this.selectedSamples.length; i++) {
        const sample = this.selectedSamples[i];

        if (sample) {
          // Generate barcode content if missing (for reopened drafts)
          if (!sample.barcodeContent) {
            const firstResult = sample.ihLabResults?.[0];
            if (firstResult) {
              const agentGroupId = firstResult.agentGroupId;
              if (!agentGroupId) continue;
              try {
                const agentGroup = await firstValueFrom(this.agentGroupService.getById(agentGroupId));
                if (agentGroup) {
                  const date = (sample.startDate as string).slice(0, 10).replace(/-/g, '');
                  const lastSampleNumber = sample.sampleNumber.length <= 4 ? sample.sampleNumber : sample.sampleNumber.slice(-4);
                  sample.barcodeContent = `${agentGroup.casId} ${date} ${lastSampleNumber}`;
                }
              } catch (error) {
              }
            }
          }

          // Now populate maps if barcode content exists
          if (sample.barcodeContent) {
            barcodeRowsMap.set(i, {
              sampleNumber: sample.sampleNumber,
              barcodeContent: sample.barcodeContent
            });

            try {
              const imgData = await this.pdfExportService.generateBarcodeImageData(sample.barcodeContent ?? sample.sampleNumber);
              barcodeImageData.set(i, imgData);
            } catch (error) {
            }
          }
        }
      }
    }

    // Create wrapper map with image data for didDrawCell callback
    const barcodeDataWithImages = new Map<number, { sampleNumber: string; imgData: string }>();
    barcodeRowsMap.forEach((value, key) => {
      const imgData = barcodeImageData.get(key);
      if (imgData) {
        barcodeDataWithImages.set(key, {
          sampleNumber: value.sampleNumber,
          imgData: imgData
        });
      }
    });

    // Build table columns dynamically - exclude the alternate sample number for LIMS to provide more space for barcode
    const tableColumns = [
      this.translate.getDynamicTranslation('Sample Number'),
      ...(isLimsIntegration ? [] : [this.alternateSampleLabel]),
      this.translate.getDynamicTranslation('Agent Name'),
      this.translate.getDynamicTranslation('Agent Group'),
      this.translate.getDynamicTranslation('Sampling Date'),
      this.translate.getDynamicTranslation('Sample Volume (L)'),
      this.translate.getDynamicTranslation('Total Time (Minutes)'),
      this.translate.getDynamicTranslation('Flowrate (L/min)'),
      this.translate.getDynamicTranslation('Method Reference')
    ];

    const tableData = this.selectedSamples.map(s => [
      s.sampleNumber,
      ...(isLimsIntegration ? [] : [s.alternateSampleNumber]),
      this.joinAgentNames(s.ihLabResults),
      s.agentGroupName,
      this.dateFormatService.convertUTCDateStringToUserTimeZoneString(s.startDate as string, this.userDateWithTimeFormat),
      this.formatVolumeLiters(s.sampleVolume),
      this.formatRunTimeMinutes(s.runTime),
      this.formatFlowRateLPerMin(s.sampleAverageCal),
      s.analyticalMethodName
    ]);

    // For LIMS, increase Sample Number column width to display barcode better
    const columnStyles: Record<number, { cellWidth: number }> = isLimsIntegration ? { 0: { cellWidth: 160 } } : {};

    await this.pdfExportService.addTable(
      doc,
      tableColumns,
      tableData,
      {
        startY: y,
        repeatTHead: true,
        headStyles: {
          fillColor: [235, 240, 250],
          textColor: [0, 0, 0],
          fontSize: 10,
          halign: 'center',
          valign: 'middle'
        },
        bodyStyles: {
          fontSize: 10,
          cellPadding: 5,
          valign: 'middle',
          minCellHeight: isLimsIntegration ? 70 : 50
        },
        columnStyles: columnStyles,
        didDrawCell: (data: any) => {
          if (data.column.index === 0 && data.row.section === 'body') {
            const rowIndex = data.row.index;
            const barcodeData = barcodeDataWithImages.get(rowIndex);
            if (barcodeData) {
              const cell = data.cell;
              this.pdfExportService.drawBarcodeInCell(doc, barcodeData.sampleNumber, barcodeData.imgData, cell.x, cell.y, cell.width, cell.height);
            }
          }
        }
      } as any
    );

    y = (doc as any).lastAutoTable.finalY + 15;

    const signatureTableHeight = 3 * 45 + 30;
    const pageHeightForSig = doc.internal.pageSize.getHeight();
    if (y + signatureTableHeight > pageHeightForSig - 40) {
      doc.addPage();
      y = 40;
    }

    await this.pdfExportService.addTable(
      doc,
      ['', this.translate.getDynamicTranslation('Print Name / Signature'), '', '', '', this.translate.getDynamicTranslation('Print Name / Signature'), '', ''],
      [
        [this.translate.getDynamicTranslation('Chain Of Custody'), '', this.translate.getDynamicTranslation('Date'), this.translate.getDynamicTranslation('Time'), '', '', this.translate.getDynamicTranslation('Date'), this.translate.getDynamicTranslation('Time')],
        [this.translate.getDynamicTranslation('Relinquished By') + ':', this.relinquishedBySignature, this.relinquishedByDate, this.relinquishedByTime, this.translate.getDynamicTranslation('Received By') + ':', '', '', ''],
        [this.translate.getDynamicTranslation('Relinquished By') + ':', '', '', '', this.translate.getDynamicTranslation('Received By') + ':', '', '', '']
      ],
      {
        startY: y,
        headStyles: {
          fillColor: [235, 240, 250],
          textColor: [0, 0, 0],
          fontSize: 10,
          halign: 'center',
          valign: 'middle',
          cellPadding: 3
        },
        bodyStyles: {
          minCellHeight: 45,
          fontSize: 10,
          valign: 'middle',
          cellPadding: 3
        }
      }
    );

    y = (doc as any).lastAutoTable.finalY + 15;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const labDetailsText = [this.labDetails.labName, this.labDetails.labAddress1, this.labDetails.labAddress2, this.labDetails.labCity, this.labDetails.labState, this.labDetails.labZip].filter(Boolean).join(', ') + (this.labDetails.labPhoneNumber ? ', ' + this.labDetails.labPhoneNumber : '') + (this.labDetails.labCellNumber ? ' | ' + this.labDetails.labCellNumber : '');
    doc.text(labDetailsText, pageWidth - pageMargin - 10, y, { align: 'right' });

    return doc.output('blob');
  }

  async saveProgress(viewPdf = false, finalizeAndPrint = false) {
    this.loading = true;
    try {
      const draftFields = {
        ...this.labRequisitionFields,
        accountNumber: this.labRequisitionFields.accountNumber ?? this.labRequisitionFields.labAccountNumber,
        tatId: this.labRequisitionFields.tatId || null,
        requisitionStatus: LabRequisitionStatus.Draft
      };

      let request$: Observable<LabRequisition | Object>;
      if (this.labRequisitionFields.id) {
        request$ = this.labRequisitionService.update(draftFields);
      } else {
        // Create must not send `id`: the server assigns the PK and the API's strict Guid
        // JSON binder rejects `id: ''` with a 400 before the action runs. Because `draftFields`
        // is a pre-built variable, TS's excess-property check does not enforce the
        // `CreatePayload<T> = Omit<T, 'id'>` guard, so strip `id` explicitly here.
        const { id, ...createPayload } = draftFields;
        request$ = this.labRequisitionService.create(createPayload);
      }

      const draftRes = await firstValueFrom(request$) as LabRequisition;

      this.labRequisitionFields.id = this.labRequisitionFields.id || draftRes.id;
      this.labRequisitionFields.labRequisitionNumber = draftRes?.labRequisitionNumber || this.labRequisitionFields.labRequisitionNumber

      if (!viewPdf) {
        this.toastService.showSuccess('Progress Saved Successfully', true);
        this.eventService.broadcast(EventKeys.flyoutClose, {
          ...draftFields,
          ...draftRes,
          id: this.labRequisitionFields.id,
          labRequisitionNumber: this.labRequisitionFields.labRequisitionNumber
        });
        return;
      }

      const pdfBlob = await this.generatePdfBlob('landscape');

      if (!finalizeAndPrint) {
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        return;
      }

      const fileKey = generateGUID();
      const signedUrl = await firstValueFrom(
        this.uploadService.getAssetsPreSignedURL(fileKey, AssetTypeEnum.IHAttachments)
      );
      await firstValueFrom(this.uploadService.uploadFile(signedUrl, pdfBlob));

      const payload: CreatePayload<FileAttachment> = {
        name: `${fileKey}.pdf`,
        fileKey,
        description: 'coc-pdf',
        organizationLevelId: this.labRequisitionFields.organizationLevelId,
        fileType: FileTypeEnum.Pdf,
        documentType: DocumentType.ChainOfCustody
      };

      const file = await firstValueFrom(this.fileAttachmentService.create(payload));

      const completeFields = {
        ...this.labRequisitionFields,
        fileAttachmentIds: [file.id],
        tatId: this.labRequisitionFields.tatId || null,
        requisitionStatus: LabRequisitionStatus.Completed
      };

      await firstValueFrom(this.labRequisitionService.update(completeFields));

      // DASH-2722: on finalize the user also gets the empty results template alongside the COC PDF. Best-effort —
      // the requisition is already Completed, so a template failure must not roll that back or block the flow.
      await this.downloadResultTemplate();

      this.toastService.showSuccess('Lab Requisition Completed Successfully', true);
      this.eventService.broadcast(EventKeys.flyoutClose, completeFields);

      if (!openFileInNewTab(file?.url)) {
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (err) {
      this.toastService.showError(err);
    } finally {
      this.loading = false;
    }
  }

  // DASH-2722: regenerate (never persist) the empty lab-result template for the finalized requisition and hand it
  // to the browser. Swallows its own errors so a template hiccup can't undo the already-Completed COC.
  private async downloadResultTemplate(): Promise<void> {
    if (!this.labRequisitionFields.id) return;
    // The template endpoint is Admin-only on the BE (mirrors the import-results gate). A non-Admin request would
    // 403 and fire a spurious error toast right after the just-Completed COC, making a fine COC look broken.
    // Skip the best-effort download for users who cannot call the endpoint.
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || ![SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType)) return;
    try {
      const blob = await firstValueFrom(this.labResultImportService.downloadTemplate(this.labRequisitionFields.id));
      const number = this.labRequisitionFields.labRequisitionNumber || String(this.labRequisitionFields.id);
      downloadBlob(blob, labResultTemplateFileName(number));
    } catch {
      this.toastService.displayErrorMessage('Lab result template could not be generated', true);
    }
  }

  public trackById = trackById;

  formatVolumeLiters(sampleVolume: number | null): string {
    if (sampleVolume == null) return '-';
    return this.valueConversionService.convertMlPerMinToLPerMin(sampleVolume).toFixed(2);
  }

  formatRunTimeMinutes(runTime: number | null): string {
    if (runTime == null) return '-';
    return this.valueConversionService.convertSecondsToMinutes(runTime).toFixed(0);
  }

  formatFlowRateLPerMin(sampleAverageCal: number | null): string {
    if (sampleAverageCal == null) return '-';
    return this.valueConversionService.convertMlPerMinToLPerMin(sampleAverageCal).toFixed(3);
  }

  formatSamplingDate(startDate: Date | string | number): string {
    if (!startDate) return '-';
    return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(startDate as string, this.userDateWithTimeFormat);
  }

  private toSampleVm(sample: Sample): SampleVm {
    return {
      ...sample,
      agentNamesStr: this.getUniqueAgentNames(sample.ihLabResults),
      samplingDateStr: this.formatSamplingDate(sample.startDate),
      volumeLitersStr: this.formatVolumeLiters(sample.sampleVolume),
      runTimeMinutesStr: this.formatRunTimeMinutes(sample.runTime),
      flowRateLPerMinStr: this.formatFlowRateLPerMin(sample.sampleAverageCal)
    };
  }

}
